import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useDebounce } from "react-use";
import {Radio, Select, Input, Button, Modal} from "antd";
import LoadingBanner from "../../components/LoadingBanner";
import TalentProvider from "../../providers/TalentProvider";
import TalentTile, { InvitedTalentTile } from "../talent/TalentTile";
import { useTalentPool } from "../../providers/TalentPoolProvider";
import TablePagination from "../../components/TablePagination";
import EmptyFeedback from "../../components/EmptyFeedback";
import ComponentError from "../../components/errors/ComponentError";
import InviteByEmailModal from "./components/InviteByEmailModal";
import InviteByGroupModal from "./components/InviteByGroupModal";
import BlockedTalentWelcome from "./BlockedTalentWelcome";
import RequisitionsList from "./components/RequisitionsList";
import RehireTalentModal from "./RehireTalentModal";
import { useAuth } from "../../providers/AuthProvider";
import TalentTileRedesign, { InvitedTalentTileRedesign } from "../talent/TalentTileRedesign";

const EMPTY_MESSAGE = {
    "-1": 'Favorited talent will appear here',
    "-2": 'Past hired talent will appear here',
    "-3": 'Invited talent will appear here',
    "-10": 'Blocked talent will appear here',
    "all": 'No talent added yet',
}

const TalentPoolManage = () => {
    const { talentPoolId } = useParams();
    const { auth } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showingActiveTalent, setShowingActiveTalent] = useState(null)
    const [showBlockedWelcome, setShowBlockedWelcome] = useState(false)
    // Talent selected for a "Rehire" (record an already-agreed engagement)
    const [talentToRehire, setTalentToRehire] = useState(null)
    const {
        talentPool,
        isLoading,
        refetch,
        error,
        onResetMemberData,
        isDisabled,
        onChangeSort,
        onUpdateTalent,
        onRemoveTalentFromPool,
        SORT_OPTIONS_MEMBERS
    } = useTalentPool();

    useEffect(() => {
        console.log('TalentPoolManage', {talentPoolId, talentPool})
        if (!isLoading  && !!talentPool && !!talentPoolId) {
            setShowingActiveTalent(talentPoolId !== 'bring-your-own-talent' || (talentPool?.talent_users?.length > 0))
            setInputValue('')


            console.log('TalentPoolManage', {talentPoolId, talentPool})
            // Check if we should show the BlockedTalentWelcome
            if (talentPoolId === 'blocked-talent' && talentPool?.talent_users?.length === 0) {
                // console.log('', )
                // const preferences = TB.storedPreferences();
                setShowBlockedWelcome(true);
            } else {
                setShowBlockedWelcome(false);
            }
        }
    }, [isLoading, talentPool,talentPoolId])

    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');

    const groupType = useMemo(() => {
        if (!talentPool) return '';
        if (talentPool.is_default) {
            return talentPool.isWrippleRecommendedPool?.() ? talentPool.name : TB.startCase(talentPoolId)
        }
        if (talentPool.is_personal) return 'Personal Talent Pool';
        return 'Company Talent Pool';
    }, [talentPool])

    /**
     * "Rehire" records an engagement the client already agreed to off-platform with someone in
     * their own network. Two conditions:
     *
     *  - the bring-your-own-talent pool. These are freelancers the client brought onto Wripple and
     *    works with regularly, so there's an existing relationship (and an already-negotiated rate)
     *    to record against.
     *  - the user can manage clients. Every route out of this modal is a Client Lead capability:
     *    recording pre-agreed terms bypasses the talent's confirmation, and change orders are
     *    rejected outright by ChangeOrdersController::store for anyone else. With neither path
     *    available to them, the button is hidden rather than opening a modal with nothing in it.
     */
    const canRehireTalent = useMemo(() => {
        return !!talentPool?.isTalentInvitePool?.() && !!auth?.canManageClients?.();
    }, [talentPool, auth])

    const [, cancel] = useDebounce(
        () => {
            // setDebouncedValue(inputValue);
            const params = new URLSearchParams(searchParams);
            if (inputValue.length) {
                params.set('search', inputValue);
            } else {
                params.delete('search');
            }
            return setSearchParams(params, { replace: true });
        }, 600, [inputValue]
    );

    // Show BlockedTalentWelcome if it's the blocked talent pool, empty, and not dismissed
    if (showBlockedWelcome) {
        return <BlockedTalentWelcome />;
    }

    return (
        <div className="talent-pool-manage">

            <div className="">

                {!isLoading && error && (
                    <ComponentError>
                        <button
                            type="button"
                            className="btn btn-primary mt-2"
                            onClick={refetch}
                        >
                            Try Again
                        </button>
                    </ComponentError>
                )}

                {(isLoading && !error) ? (
                    <LoadingBanner />
                ) : !error && (

                    <div className="flex flex-col items-stretch gap-4">
                        {/* Only shown on Talent Invitations page */}

                        {talentPoolId === 'bring-your-own-talent' && (
                            <div className="flex flex-col items-center mb-2">
                                <Radio.Group
                                    className="invitations-toggle-radio"
                                    options={[
                                        {
                                            label: `Awaiting Acceptance (${talentPool?.talent_invites?.length || 0})`,
                                            value: false,
                                        }, {
                                            label: `Accepted (${talentPool?.talent_users?.length || 0})`,
                                            value: true,
                                        }
                                    ]}
                                    onChange={async (e) => {
                                        if (!showingActiveTalent && e.target.value) {
                                            await onResetMemberData();
                                        }
                                        console.log('e', e.target.value)
                                        return setShowingActiveTalent(e.target.value)
                                    }}
                                    value={showingActiveTalent}
                                    disabled={isDisabled}
                                    optionType="button"
                                    buttonStyle="solid"
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-4 justify-between items-stretch">

                            {!showingActiveTalent ? (
                                <React.Fragment>
                                    <p className="text-primary font-bold text-base">
                                        Awaiting Acceptance&nbsp;
                                        <span className="text-secondary font-bold">
                                            ({talentPool?.talent_invites?.length})
                                        </span>
                                    </p>
                                    <div className="flex flex-wrap gap-6 justify-center">
                                        {!talentPool?.talent_invites?.length ? (
                                            <EmptyFeedback>

                                                <div className="flex flex-col items-center">
                                                    <p className="text-sm font-medium text-center pb-6">
                                                        Invited talent will appear here
                                                    </p>

                                                    <div className="flex flex-row items-center gap-3">
                                                        <InviteByEmailModal useDarkTheme={true}/>
                                                        <InviteByGroupModal />
                                                    </div>
                                                </div>

                                            </EmptyFeedback>
                                        ) : talentPool?.talent_invites?.map((user, index) => {
                                            return (
                                                <div key={`${user.email}-${index}`} className="max-w-[342px] min-w-[342px]">
                                                    <TalentProvider
                                                        talent={user}
                                                        isDisabled={isDisabled}
                                                        onUpdated={updatedTalent => {
                                                            console.log('onUpdated', updatedTalent)
                                                            onUpdateTalent(updatedTalent)
                                                        }}
                                                        criteria={{}}
                                                    >
                                                        {/* <InvitedTalentTile onRemoveFromPool={!talentPool.canAddRemoveTalent() ? undefined : async () => {
                                                            let updated = await onRemoveTalentFromPool(user);
                                                            if (updated) {
                                                                await setInputValue('');
                                                            }
                                                        }}/> */}

                                                          <InvitedTalentTileRedesign>
                                                            <div className="w-full pt-5 pb-1 flex flex-col gap-3 content-evenly">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-block mb2"
                                                                    disabled={isDisabled || !talentPool.canAddRemoveTalent()}
                                                                    onClick={async () => {
                                                                        let updated = await onRemoveTalentFromPool(user);
                                                                        if (updated) {
                                                                            await setInputValue('');
                                                                        }}
                                                                    }
                                                                >
                                                                    Remove from Pool
                                                                </button>
                                                            </div>
                                                        </InvitedTalentTileRedesign>
                                                    </TalentProvider>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {/* Show Reqs section for custom talent pools (not default pools) */}
                                    {talentPool && !talentPool.is_default && talentPool.id > 0 && talentPool.talent_users_count > 0 && (
                                        <RequisitionsList
                                            talentPool={talentPool}
                                            onReqCreated={(project) => {
                                                console.log('Req created:', project);
                                                // Optionally refetch or update state
                                            }}
                                        />
                                    )}

                                    <Input
                                        size="large"
                                        className="w-full p-3"
                                        placeholder="Search By Name"
                                        value={inputValue}
                                        disabled={isLoading || !talentPool?.talent_users_count}
                                        onChange={({ currentTarget }) => setInputValue(currentTarget.value)}
                                    />
                                    <div className="flex flex-row justify-between items-center">
                                        {talentPoolId === 'bring-your-own-talent' ? (
                                            <p className="text-primary font-bold text-base">
                                                Accepted&nbsp;
                                                <span className="text-secondary font-bold">
                                                    ({talentPool?.talent_users_count})
                                                </span>
                                            </p>
                                        ) : (
                                            <p className="text-primary font-bold text-lg">
                                                {groupType}&nbsp;
                                                {!!talentPool?.talent_users_count && talentPoolId !== 'bring-your-own-talent' && (
                                                    <span className="text-secondary font-bold">
                                                        ({talentPool?.talent_users_count})
                                                    </span>
                                                )}
                                            </p>
                                        )}

                                        <div className="flex flex-row gap-1 items-center">
                                            <p>Sort:</p>
                                            <Select
                                                defaultValue="avg_review_rating"
                                                value={SORT_OPTIONS_MEMBERS[searchParams.get('sort_by') || 'avg_review_rating']}
                                                onChange={(key) => onChangeSort(key)}
                                                bordered={false}
                                                options={TB.map(SORT_OPTIONS_MEMBERS, (label, value) => ({
                                                    value,
                                                    label
                                                }))}
                                                disabled={isLoading}
                                                popupClassName="min-w-[210px]"
                                                getPopupContainer={() => document.getElementById('talent-pool-manage-container')}
                                                className="text-primary"
                                                placement="bottomRight"
                                            />
                                        </div>
                                    </div>
                                    {talentPool && !isLoading && (
                                        <TablePagination
                                            isDisabled={isLoading}
                                            currentPage={talentPool.current_page}
                                            useParams
                                            pagesCount={talentPool.last_page}
                                            total={talentPool.total}
                                            perPage={talentPool.per_page}
                                        />
                                    )}
                                    <div className="browse-list full-card-list">

                                        {!talentPool?.talent_users?.length ? (
                                            <EmptyFeedback>
                                                {!!new URLSearchParams(searchParams).get('search') ? (
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-sm font-medium text-center pb-6">
                                                            No talent found in your search
                                                        </p>

                                                        <button
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                setInputValue('')
                                                                const params = new URLSearchParams(searchParams);
                                                                params.delete('search');
                                                                setSearchParams(params, { replace: true });
                                                            }}
                                                        >
                                                            Reset Search
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-sm font-medium text-center pb-6">
                                                            {EMPTY_MESSAGE[talentPool?.id < 0 ? talentPool?.id : 'all']}
                                                        </p>

                                                        {talentPool?.id > 0 && (
                                                            <Link
                                                                type="button"
                                                                to={`/client/talent-pool/${talentPool.id}/add-talent`}
                                                                className="btn btn-primary flex gap-2"
                                                            >
                                                                <i className="fas fa-plus" /> Add Talent
                                                            </Link>
                                                        )}
                                                    </div>
                                                )}
                                            </EmptyFeedback>
                                        ) : (
                                            <div className="flex flex-wrap gap-6 justify-center">
                                                <div className="flex flex-wrap gap-6 justify-center">
                                                    {talentPool?.talent_users?.map(user => (
                                                        <div key={`tal-${user.id}`}
                                                            className="max-w-[342px] min-w-[342px] flex flex-col items-stretch">
                                                            <TalentProvider
                                                                talent={user}
                                                                onUpdated={updatedTalent => {
                                                                    console.log('onUpdated', updatedTalent)
                                                                    onUpdateTalent(updatedTalent)
                                                                }}
                                                                isDisabled={isDisabled}
                                                                canFavorite={talentPoolId !== 'blocked-talent'}
                                                                criteria={{}}
                                                            >
                                                                <TalentTileRedesign
                                                                    includeRating={talentPoolId !== 'blocked-talent'}
                                                                    isBlocked={talentPoolId === 'blocked-talent'}
                                                                    onRequest={(requestCallback) => requestCallback()}
                                                                    onUpdated={updatedTalent => {
                                                                        console.log('onUpdated', updatedTalent)
                                                                        onUpdateTalent(updatedTalent)
                                                                    }}
                                                                    isDisabled={isLoading || isDisabled}
                                                                    placeholder={isLoading}
                                                                    // onRemoveFromPool={}
                                                                >
                                                                    <div className="w-full pt-2 pb-1 flex flex-col gap-3 content-evenly">
                                                                        {canRehireTalent && (
                                                                            <React.Fragment>
                                                                                <p className="text-xxs text-gray-500 text-center">
                                                                                    Already agreed on new work?<br/>
                                                                                    Record it and we'll handle the SOW.
                                                                                </p>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-success btn-block mb-0"
                                                                                    disabled={isLoading || isDisabled}
                                                                                    onClick={() => setTalentToRehire(user)}
                                                                                >
                                                                                    <i className="fas fa-handshake mr-2"/>
                                                                                    Rehire
                                                                                </button>

                                                                            </React.Fragment>
                                                                        )}
                                                                        <Button disabled={isDisabled || !talentPool.canAddRemoveTalent()} type={talentPoolId === 'blocked-talent' ? "default" : "primary"}  onClick={async () => {
                                                                            let updated = await onRemoveTalentFromPool(user);
                                                                            if (updated) {
                                                                                await setInputValue('');
                                                                            }
                                                                        }}>
                                                                            {talentPoolId === 'blocked-talent' ? "Remove from Blocked Talent" :"Remove from Pool"}
                                                                        </Button>
                                                                    </div>

                                                                </TalentTileRedesign>
                                                            </TalentProvider>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {talentPool && !isLoading && (
                                        <TablePagination
                                            isDisabled={isLoading}
                                            currentPage={talentPool.current_page}
                                            useParams
                                            pagesCount={talentPool.last_page}
                                            total={talentPool.total}
                                            perPage={talentPool.per_page}
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Rehire Modal — record an already-agreed engagement with in-network talent */}
            <Modal
                /* Gated on canRehireTalent too, not just on the button being hidden — so a stale
                   selection can't leave the modal open if permissions change mid-session. */
                open={!!talentToRehire && canRehireTalent}
                onCancel={() => setTalentToRehire(null)}
                closable={false}
                footer={false}
                destroyOnClose={true}
                wrapClassName="[&_.ant-modal-body]:p-0"
                title={false}
                /*
                 * Sits below TB.alert's overlay, which is pinned at `z-index: 200 !important`
                 * (less/_alerts.less) and renders at App level rather than inside this modal — so a
                 * descendant selector here can't reach it and the !important can't be overridden by
                 * a utility class. Dropping the modal underneath is how CreateChangeOrderForm
                 * solves the same problem, and it lets confirmations from inside the flow (such as
                 * picking an engagement that already has a draft change order) appear on top.
                 */
                zIndex={199}
                width="90%"
            >
                {!!talentToRehire && (
                    <RehireTalentModal
                        talent={talentToRehire}
                        talentPool={talentPool}
                        onSuccess={async () => {
                            setTalentToRehire(null);
                            TB.toast('Rehire recorded — SOW requested!');
                            await onResetMemberData?.();
                        }}
                        onClose={() => setTalentToRehire(null)}
                    />
                )}
            </Modal>
        </div>
    )
}

export default TalentPoolManage;